import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useIncomingNotifications } from "../../../contexts/NotificationsContext";
import {
  isWindowClosedError,
  whatsappService,
} from "../../../services/whatsapp.service";
import { extractApiErrorMessage } from "../../../utils/apiError";

export const REFRESH_INTERVAL_MS = 15000;
export const THREADS_ERROR_MESSAGE =
  "No se pudieron cargar las conversaciones de WhatsApp.";
export const MESSAGES_ERROR_MESSAGE = "No se pudo cargar la conversación.";
export const SEND_ERROR_MESSAGE = "No se pudo enviar el mensaje.";
export const MEDIA_ERROR_MESSAGE = "No se pudo enviar el archivo.";

const sameId = (left, right) =>
  left != null && right != null && Number(left) === Number(right);

export const mergeThread = (threads, updated) => {
  if (!updated?.id) return threads;
  const exists = threads.some((thread) => sameId(thread.id, updated.id));
  if (!exists) return [updated, ...threads];
  return threads.map((thread) =>
    sameId(thread.id, updated.id) ? { ...thread, ...updated } : thread,
  );
};

const patchThread = (threads, threadId, changes) =>
  threads.map((thread) =>
    sameId(thread.id, threadId) ? { ...thread, ...changes } : thread,
  );

const appendMessage = (messages, message) => {
  if (!message?.id) return messages;
  if (messages.some((existing) => sameId(existing.id, message.id))) {
    return messages.map((existing) =>
      sameId(existing.id, message.id) ? { ...existing, ...message } : existing,
    );
  }
  return [...messages, message];
};

const chronologically = (left, right) => {
  const byTime = new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime();
  return byTime || Number(left.id) - Number(right.id);
};

export const mergeLatestPage = (previous, latest) => {
  if (!previous.length) return latest;
  const latestById = new Map(latest.map((message) => [Number(message.id), message]));
  const merged = previous.map((message) =>
    latestById.has(Number(message.id))
      ? { ...message, ...latestById.get(Number(message.id)) }
      : message,
  );
  const knownIds = new Set(previous.map((message) => Number(message.id)));
  const appended = latest.filter((message) => !knownIds.has(Number(message.id)));
  return [...merged, ...appended].sort(chronologically);
};

const summaryOf = (message) => ({
  id: message.id,
  direction: message.direction,
  message_type: message.message_type,
  preview: message.preview ?? message.body ?? "",
  status: message.status,
  timestamp: message.timestamp,
});

const isWhatsAppNotificationForLead = (notification, leadId) => {
  const payload = notification?.payload;
  if (!payload || payload.type !== "whatsapp_message") return false;
  if (payload.lead_id == null) return true;
  if (Array.isArray(payload.lead_ids) && payload.lead_ids.some((id) => sameId(id, leadId))) return true;
  return sameId(payload.lead_id, leadId);
};

export function useLeadWhatsApp({ leadId, initialThreadId = null, enabled = true }) {
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [creating, setCreating] = useState(false);

  const subscribeToIncoming = useIncomingNotifications();
  const selectedThreadIdRef = useRef(null);
  const loadedLeadIdRef = useRef(null);
  const markingReadRef = useRef(new Set());
  const honoredInitialThreadRef = useRef(null);
  const latestThreadsRequestRef = useRef(0);

  const loadThreads = useCallback(
    async ({ silent = false } = {}) => {
      if (!leadId) return null;
      const requestId = latestThreadsRequestRef.current + 1;
      latestThreadsRequestRef.current = requestId;
      const isLatest = () => latestThreadsRequestRef.current === requestId;
      if (!silent) {
        setThreadsLoading(true);
        setThreadsError(null);
      }
      try {
        const { results } = await whatsappService.listLeadThreads(leadId);
        if (!isLatest()) return null;
        setThreads(results);
        setThreadsError(null);
        return results;
      } catch (requestError) {
        if (!isLatest()) return null;
        if (!silent) {
          setThreadsError(
            extractApiErrorMessage(requestError) || THREADS_ERROR_MESSAGE,
          );
        }
        return null;
      } finally {
        if (!silent) setThreadsLoading(false);
      }
    },
    [leadId],
  );

  const markRead = useCallback(async (thread) => {
    if (!thread?.id || !(thread.unread_count > 0)) return;
    if (markingReadRef.current.has(thread.id)) return;
    markingReadRef.current.add(thread.id);
    try {
      const updated = await whatsappService.markThreadRead(thread.id);
      setThreads((previous) =>
        mergeThread(previous, { ...(updated || {}), id: thread.id, unread_count: 0 }),
      );
    } catch (requestError) {
      console.error("No se pudo marcar la conversación como leída:", requestError);
    } finally {
      markingReadRef.current.delete(thread.id);
    }
  }, []);

  const loadMessages = useCallback(
    async (threadId, { silent = false } = {}) => {
      if (!threadId) return;
      if (!silent) {
        setMessagesLoading(true);
        setMessagesError(null);
      }
      try {
        const { results, hasMore: more, thread } =
          await whatsappService.listMessages(threadId);
        if (!sameId(selectedThreadIdRef.current, threadId)) return;
        setMessages((previous) =>
          silent ? mergeLatestPage(previous, results) : results,
        );
        setMessagesError(null);
        if (!silent) setHasMore(more);
        if (thread) {
          setThreads((previous) => mergeThread(previous, thread));
          if (silent && thread.unread_count > 0) markRead(thread);
        }
      } catch (requestError) {
        if (!silent && sameId(selectedThreadIdRef.current, threadId)) {
          setMessagesError(
            extractApiErrorMessage(requestError) || MESSAGES_ERROR_MESSAGE,
          );
        }
      } finally {
        if (!silent && sameId(selectedThreadIdRef.current, threadId)) {
          setMessagesLoading(false);
        }
      }
    },
    [markRead],
  );

  const selectThread = useCallback(
    (thread) => {
      if (!thread?.id) return;
      selectedThreadIdRef.current = thread.id;
      setSelectedThreadId(thread.id);
      setMessages([]);
      setHasMore(false);
      setSendError(null);
      loadMessages(thread.id);
      markRead(thread);
    },
    [loadMessages, markRead],
  );

  const clearSelection = useCallback(() => {
    selectedThreadIdRef.current = null;
    setSelectedThreadId(null);
    setMessages([]);
    setHasMore(false);
    setSendError(null);
  }, []);

  useEffect(() => {
    loadedLeadIdRef.current = null;
    selectedThreadIdRef.current = null;
    honoredInitialThreadRef.current = null;
    setThreads([]);
    setThreadsError(null);
    setSelectedThreadId(null);
    setMessages([]);
    setMessagesError(null);
    setHasMore(false);
    setSendError(null);
  }, [leadId]);

  useEffect(() => {
    if (!enabled || !leadId) return undefined;
    if (sameId(loadedLeadIdRef.current, leadId)) return undefined;
    loadedLeadIdRef.current = leadId;

    let cancelled = false;
    loadThreads().then((results) => {
      if (cancelled || !results?.length) return;
      if (selectedThreadIdRef.current) return;
      const preferred =
        initialThreadId != null
          ? results.find((thread) => sameId(thread.id, initialThreadId))
          : null;
      selectThread(preferred ?? results[0]);
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, leadId, initialThreadId, loadThreads, selectThread]);

  useEffect(() => {
    if (!enabled || initialThreadId == null) return;
    if (sameId(honoredInitialThreadRef.current, initialThreadId)) return;
    const requested = threads.find((thread) => sameId(thread.id, initialThreadId));
    if (!requested) return;
    honoredInitialThreadRef.current = initialThreadId;
    if (!sameId(selectedThreadIdRef.current, initialThreadId)) {
      selectThread(requested);
    }
  }, [enabled, initialThreadId, threads, selectThread]);

  const refresh = useCallback(() => {
    loadThreads({ silent: true });
    if (selectedThreadIdRef.current) {
      loadMessages(selectedThreadIdRef.current, { silent: true });
    }
  }, [loadThreads, loadMessages]);

  useEffect(() => {
    if (!enabled || !leadId) return undefined;
    const timer = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, leadId, refresh]);

  useEffect(() => {
    if (!enabled || !leadId || typeof subscribeToIncoming !== "function") {
      return undefined;
    }
    return subscribeToIncoming((notification) => {
      if (!isWhatsAppNotificationForLead(notification, leadId)) return;
      refresh();
    });
  }, [enabled, leadId, subscribeToIncoming, refresh]);

  const deliver = useCallback(async (send, fallbackMessage) => {
    const threadId = selectedThreadIdRef.current;
    if (!threadId) return false;
    setSending(true);
    setSendError(null);
    try {
      const message = await send(threadId);
      if (sameId(selectedThreadIdRef.current, threadId)) {
        setMessages((previous) => appendMessage(previous, message));
      }
      setThreads((previous) =>
        patchThread(previous, threadId, {
          last_message_at: message.timestamp,
          last_message: summaryOf(message),
        }),
      );
      return true;
    } catch (requestError) {
      if (isWindowClosedError(requestError)) {
        setThreads((previous) =>
          patchThread(previous, threadId, { window_open: false }),
        );
      }
      setSendError(extractApiErrorMessage(requestError) || fallbackMessage);
      return false;
    } finally {
      setSending(false);
    }
  }, []);

  const sendText = useCallback(
    (body) => deliver((threadId) => whatsappService.sendText(threadId, body), SEND_ERROR_MESSAGE),
    [deliver],
  );

  const sendMedia = useCallback(
    ({ file, attachmentId, caption = "" }) =>
      deliver(
        (threadId) => whatsappService.sendMedia(threadId, { file, attachmentId, caption }),
        MEDIA_ERROR_MESSAGE,
      ),
    [deliver],
  );

  const sendTemplate = useCallback(async (payload) => {
    const threadId = selectedThreadIdRef.current;
    if (!threadId) return null;
    setSending(true);
    setSendError(null);
    try {
      const message = await whatsappService.sendTemplate(threadId, payload);
      if (sameId(selectedThreadIdRef.current, threadId)) {
        setMessages((previous) => appendMessage(previous, message));
      }
      setThreads((previous) =>
        patchThread(previous, threadId, {
          last_message_at: message.timestamp,
          last_message: summaryOf(message),
        }),
      );
      return message;
    } finally {
      setSending(false);
    }
  }, []);

  const loadOlder = useCallback(async () => {
    const threadId = selectedThreadIdRef.current;
    const oldest = messages[0];
    if (!threadId || !oldest?.id || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const { results, hasMore: more } = await whatsappService.listMessages(
        threadId,
        { before: oldest.id },
      );
      if (!sameId(selectedThreadIdRef.current, threadId)) return;
      setMessages((previous) => [...results, ...previous]);
      setHasMore(more);
    } catch (requestError) {
      setMessagesError(
        extractApiErrorMessage(requestError) || MESSAGES_ERROR_MESSAGE,
      );
    } finally {
      setLoadingOlder(false);
    }
  }, [messages, loadingOlder]);

  const createThread = useCallback(
    async ({ whatsappAccountId, phone, personId = null }) => {
      setCreating(true);
      try {
        const { thread } = await whatsappService.createThread({
          leadId,
          whatsappAccountId,
          phone,
          personId,
        });
        setThreads((previous) => mergeThread(previous, thread));
        selectThread(thread);
        return thread;
      } finally {
        setCreating(false);
      }
    },
    [leadId, selectThread],
  );

  const selectedThread = useMemo(
    () => threads.find((thread) => sameId(thread.id, selectedThreadId)) ?? null,
    [threads, selectedThreadId],
  );

  return {
    threads,
    threadsLoading,
    threadsError,
    reloadThreads: loadThreads,
    selectedThread,
    selectedThreadId,
    selectThread,
    clearSelection,
    messages,
    messagesLoading,
    messagesError,
    hasMore,
    loadingOlder,
    loadOlder,
    sendText,
    sendMedia,
    sendTemplate,
    sending,
    sendError,
    clearSendError: () => setSendError(null),
    createThread,
    creating,
    refresh,
  };
}

export default useLeadWhatsApp;
